import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Col, Row, Spin } from 'antd';

import TaxInvoiceListComponent from '../../components/invoices/TaxInvoiceListComponent';
import SearchTaxInvoiceComponent from '../../components/invoices/SearchTaxInvoiceComponent';
import {
  excludeTaxInvoices,
  getTaxInvoiceList,
  importTaxInvoiceList,
  mappingInvoiceToTaxInvoice,
} from '../../api/constlink';
import { TaxInvoice } from '../../modules/invoices';
import MappingInvoiceModal from '../../components/invoices/MappingInvoiceModal';
import ImportTaxInvoiceModal from '../../components/invoices/ImportTaxInvoiceModal';
import TaxInvoiceCreateDrawer from './TaxInvoiceCreateDrawer';
import TaxInvoiceDetailDrawer from './TaxInvoiceDetailDrawer';

interface TaxInvoiceListContainerProps {
  enterpriseId: string;
}

function TaxInvoiceListContainer({
  enterpriseId,
}: TaxInvoiceListContainerProps) {
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchData, setSearchData] = useState<any>({
    startDate: dayjs().startOf('month'),
    endDate: dayjs().endOf('month'),
  });
  const [taxInvoices, setTaxInvoices] = useState<TaxInvoice[]>([]);
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalText, setModalText] = useState('');
  const [selectedTaxInvoice, setSelectedTaxInvoice] = useState<any>(null);
  const [visibleCreateDrawer, setVisibleCreateDrawer] =
    useState<boolean>(false);

  const [detailDrawerConfig, setDetailDrawerConfig] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const _data: any = await getTaxInvoiceList({
          enterpriseId,
          filterData: {
            startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
          },
        });
        setTaxInvoices(_data.taxInvoices);
        if (_data.success === false) {
          setResult({ success: false, message: _data.message });
        }
      } catch (error: any) {
        setResult({ success: false, message: error.response.data.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [enterpriseId]);

  const handleOk = async (year: string, quarter: string) => {
    let startDate = '';
    let endDate = '';

    if (quarter === '1') {
      startDate = `${year}0101`;
      endDate = `${year}0331`;
    } else if (quarter === '2') {
      startDate = `${year}0401`;
      endDate = `${year}0630`;
    } else if (quarter === '3') {
      startDate = `${year}0701`;
      endDate = `${year}0930`;
    } else if (quarter === '4') {
      startDate = `${year}1001`;
      endDate = `${year}1231`;
    }

    if (dayjs(startDate).isAfter(dayjs())) {
      startDate = dayjs().format('YYYYMMDD');
    }

    if (dayjs(endDate).isAfter(dayjs())) {
      endDate = dayjs().format('YYYYMMDD');
    }

    setModalText(
      `매입증빙(세금계산서,현금영수증) 을 불러오는 중입니다. 기간: ${dayjs(
        startDate,
      ).format('YYYY-MM-DD')} ~ ${dayjs(endDate).format('YYYY-MM-DD')}`,
    );
    setConfirmLoading(true);

    try {
      const _result = await importTaxInvoiceList({
        enterpriseId,
        filterData: {
          startDate,
          endDate,
        },
      });
      setResult(_result);
    } catch (error: any) {
      setResult({ success: false, message: error.response.data.message });
    } finally {
      setConfirmLoading(false);
      setOpen(false);
      const _data: any = await getTaxInvoiceList({
        enterpriseId,
        filterData: {
          startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
          endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
        },
      });
      setTaxInvoices(_data.taxInvoices);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const _searchTaxInvoices = async (data: any) => {
    const filterData: any = {
      startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(data.endDate).format('YYYY-MM-DD'),
    };

    if (data.companyName) {
      filterData.companyName = data.companyName;
    }

    if (data.corporateNumber) {
      filterData.corporateNumber = data.corporateNumber.replaceAll('-', '');
    }

    try {
      setLoading(true);
      const _data: any = await getTaxInvoiceList({
        enterpriseId,
        filterData,
      });
      setTaxInvoices(_data.taxInvoices);
      if (_data.success === false) {
        setResult({ success: false, message: _data.message });
      }
    } catch (error: any) {
      setResult({ success: false, message: error.response.data.message });
    } finally {
      setLoading(false);
    }

    setSearchData({
      startDate: dayjs(data.startDate),
      endDate: dayjs(data.endDate),
    });
  };

  const _initSearchForm = () => {
    setSearchData({
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month'),
    });
  };

  const importTaxInvoices = async () => {
    setModalText('홈택스에서 매입증빙(세금계산서,현금영수증) 을 불러옵니다.');
    setOpen(true);
  };

  const openMappingInvoiceModal = (taxInvoiceId: number) => {
    const index = taxInvoices.findIndex(
      (taxInvoice) => taxInvoice.id === taxInvoiceId,
    );
    if (index !== -1) {
      setSelectedTaxInvoice(taxInvoices[index]);
    } else {
      setSelectedTaxInvoice(null);
    }
  };

  const _mappingInvoiceToTaxInvoice = async (
    idList: any,
    description: string,
  ) => {
    try {
      const data: any = {
        idList,
      };

      if (description) {
        data.description = description;
      }

      const result: any = await mappingInvoiceToTaxInvoice({
        enterpriseId,
        taxInvoiceId: selectedTaxInvoice.id,
        data,
      });
      const index = taxInvoices.findIndex(
        (taxInvoice) => taxInvoice.id === result.taxInvoice.id,
      );
      if (index !== -1) {
        taxInvoices[index] = result.taxInvoice;
        setTaxInvoices([...taxInvoices]);
      }

      // setResult({ success: result.success, message: result.message });
      setSelectedTaxInvoice(null);
    } catch (error: any) {
      setResult({ success: false, message: error.response.data.message });
    }
  };

  const excludeTaxInvoice = async (idList: number[]) => {
    try {
      setLoading(true);
      const result: any = await excludeTaxInvoices({
        enterpriseId,
        idList,
      });
      if (result.success) {
        const _taxInvoices = taxInvoices.filter(
          (taxInvoice) => !idList.includes(taxInvoice.id),
        );
        setTaxInvoices(_taxInvoices);
        setResult({ success: result.success, message: result.message });
      }
    } catch (error: any) {
      setResult({ success: false, message: error.response.data.message });
    }
    setLoading(false);
  };

  const moveToCreatePage = () => {
    setVisibleCreateDrawer(true);
  };

  const _setTaxInvoiceId = (taxInvoiceId: number) => {
    const index = taxInvoices.findIndex(
      (taxInvoice) => taxInvoice.id === taxInvoiceId,
    );
    if (index === -1) {
      return;
    }
    const taxInvoice = taxInvoices[index];

    setDetailDrawerConfig({
      enterpriseId,
      taxInvoice,
    });
  };

  return (
    <>
      <Row justify="space-between">
        <Col span={4} style={{ marginTop: '10px' }}>
          {searchData && (
            <SearchTaxInvoiceComponent
              searchData={searchData}
              searchTaxInvoices={_searchTaxInvoices}
              initSearchForm={_initSearchForm}
            />
          )}
        </Col>
        <Col span={20}>
          {result && (
            <Row>
              <Col span={24}>
                <Alert
                  message={`${result.message}`}
                  type={result.success ? 'success' : 'error'}
                  showIcon
                  closable
                />
              </Col>
            </Row>
          )}
          {loading ? (
            <Row>
              <Spin />
            </Row>
          ) : (
            taxInvoices && (
              <Row>
                <TaxInvoiceListComponent
                  data={taxInvoices}
                  importTaxInvoices={importTaxInvoices}
                  openMappingInvoiceModal={openMappingInvoiceModal}
                  excludeTaxInvoice={excludeTaxInvoice}
                  moveToCreatePage={moveToCreatePage}
                  setTaxInvoiceId={_setTaxInvoiceId}
                />
              </Row>
            )
          )}
        </Col>
      </Row>

      <ImportTaxInvoiceModal
        isOpen={open}
        confirmLoading={confirmLoading}
        modalText={modalText}
        handleOk={handleOk}
        handleCancel={handleCancel}
      />

      {selectedTaxInvoice && (
        <MappingInvoiceModal
          selectedTaxInvoice={selectedTaxInvoice}
          enterpriseId={enterpriseId}
          onCreate={_mappingInvoiceToTaxInvoice}
          onCancel={() => setSelectedTaxInvoice(null)}
        />
      )}

      <TaxInvoiceCreateDrawer
        enterpriseId={enterpriseId}
        visible={visibleCreateDrawer}
        onClose={async () => {
          const _data: any = await getTaxInvoiceList({
            enterpriseId,
            filterData: {
              startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
              endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
            },
          });
          setTaxInvoices(_data.taxInvoices);
          setVisibleCreateDrawer(false);
        }}
      />
      <TaxInvoiceDetailDrawer
        config={detailDrawerConfig}
        visible={detailDrawerConfig !== null}
        onClose={async (isTaxInvoiceChanged) => {
          if (isTaxInvoiceChanged) {
            const _data: any = await getTaxInvoiceList({
              enterpriseId,
              filterData: {
                startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
                endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
              },
            });
            setTaxInvoices(_data.taxInvoices);
          }
          setDetailDrawerConfig(null);
        }}
      />
    </>
  );
}

export default TaxInvoiceListContainer;
